import React, {
	ChangeEvent,
	useState,
	useReducer,
	FormEvent,
	useEffect,
} from 'react';
import { text, unitList } from '../data';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import {
	BearingClass,
	BearingInfo,
	BearingTypeCode,
	ClearanceCalculationPayload,
	InstallationCalculationType,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import {
	getBearingClasses,
	getFittingPracticeNavigation,
	taperedBoreMessage,
} from '../../components/bearing-search/api/api';
import { FittingCalculationResults, SelectOption } from '../types/types';
import fittingPracticeReducer, { initialFittingPracticeState } from './reducer';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import {
	getPartStateFromParams,
	getTooltip,
	toggleImportantNote,
	toggleNextTools,
	updateToolParams,
	getPartNumberFromUrl,
	toggleReportReady,
} from '../utils/engineering-tools';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import FittingForm, {
	FittingPracticeNavigationMap,
} from './fitting-form/FittingForm';
import FormLoader from '../components/FormLoader';
import { getFittingPracticeNavigationMap } from './fitting-form/navigation';
import { trackStart, updateActionParams } from '../utils/analytics';
import PartPreSearch from '../components/PartPreSearch';
import DOMPurify from "dompurify";

const FittingPractice: React.FC = () => {
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ isCalculated, setIsCalculated ] = useState<
		Record<InstallationCalculationType, boolean>
	>( {
		Inner: false,
		Outer: false,
	} );
	const [ isBearingClassesLoading, setIsBearingClassesLoading ] = useState( false );
	const [ savedBearingClass, setSavedBearingClass ] = useState( '' );
	const [ isNavigationLoading, setIsNavigationLoading ] = useState( false );
	const [ isError, setIsError ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ bearingClasses, setBearingClasses ] = useState<BearingClass[]>( [] );
	const [ fittingResults, setFittingResults ] =
		useState<FittingCalculationResults>( {
			minimumOuterFit: 0,
			maximumOuterFit: 0,
			minimumInnerFit: 0,
			maximumInnerFit: 0,
		} );
	const [ innerNav, setInnerNav ] = useState<
		FittingPracticeNavigationMap | undefined
	>();
	const [ outerNav, setOuterNav ] = useState<
		FittingPracticeNavigationMap | undefined
	>();
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ formState, dispatch ] = useReducer(
		fittingPracticeReducer,
		initialFittingPracticeState,
	);

	useEffect( () => {
		trackStart( 'bearingFittingPractice' );

		// Parse tool parameters if it's from recommended link
		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		setSavedBearingClass( queryMap.get( 'bearingClass' ) || '' );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				setSearchInput(partNumberFromUrl);
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
				handleSubmit();
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setIsPartSelected( true );
				setSearchInput( savedPart.marketPlaceDescription || '' );
			}
		}
	}, [] );

	// Show / hide recommended tools and important notice
	useEffect( () => {
		const showTools = Object.values( isCalculated ).some(
			( calculated ) => calculated,
		);

		toggleNextTools( showTools );
		toggleImportantNote( showTools );
		toggleReportReady(
			showTools,
			partState.marketPlaceDescription,
			partState.primaryImage,
		);

		return () => {
			toggleNextTools( false );
			toggleImportantNote( false );
		};
	}, [ isCalculated ] );

	// Pass partNumber and bearingClass to recommended tools link
	useEffect( () => {
		type ToolQueries = BearingPartState &
			FittingCalculationResults &
			Pick<ClearanceCalculationPayload, 'unit'>;

		updateToolParams<ToolQueries>( {
			...partState,
			...fittingResults,
			unit: formState.unit,
		} );

		updateActionParams( 'bearingFittingPractice', partState );

		return () => {
			updateToolParams();
			updateActionParams( 'bearingFittingPractice' );
		};
	}, [ fittingResults ] );

	useEffect( () => {
		if ( partState.bearingType ) {
			setIsBearingClassesLoading( true );
			setIsNavigationLoading( true );

			// Get bearing classes
			getBearingClasses( {
				bearingType: partState.bearingType,
				designUnit:
					partState.bearingType === BearingTypeCode.TRB
						? partState.designUnit
						: undefined,
			} )
				.then( ( response ) => {
					const classes = response.bearingClassList;

					if ( classes.length > 0 ) {
						setBearingClasses( classes );

						const defaultBearingClass =
							classes.find( ( item ) => item.value === savedBearingClass ) ||
							classes[ 0 ];

						dispatch( {
							type: 'change_bearing_class',
							payload: defaultBearingClass,
						} );
					}
				} )
				.finally( () => {
					setIsBearingClassesLoading( false );
				} );
		}
	}, [ partState.bearingType ] );

	useEffect( () => {
		const { bearingClass } = formState;

		// Get navigation details
		let bearingType: string = partState.bearingType;

		if ( bearingType === BearingTypeCode.TRB ) {
			bearingType = partState.bearingSubType;
		}

		if ( bearingClass ) {
			setIsNavigationLoading( true );

			getFittingPracticeNavigation( bearingType, bearingClass.value )
				.then( ( response ) => {
					const navigation = response.fittingPracticesNavigationList;

					if ( navigation?.length ) {
						const [ inner, outer ] = getFittingPracticeNavigationMap(
							response.fittingPracticesNavigationList,
						);

						setInnerNav( inner );
						setOuterNav( outer );
					} else {
						setIsError( true );
					}
				} )
				.catch( () => {
					setIsError( true );
				} )
				.finally( () => setIsNavigationLoading( false ) );
		}
	}, [ formState.bearingClass ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
		setIsPartSelected( false );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );
			setIsPartSelected( true );
			setSearchInput( part.marketPlaceDescription || '' );
		}

		setIsPartOpen( false );
	};

	const clearSearch = () => {
		setSearchInput( '' );
		setIsPartSelected( false );
		setSavedBearingClass( '' );

		setIsCalculated( {
			Inner: false,
			Outer: false,
		} );

		toggleImportantNote( false );

		setIsError( false );

		partDispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'reset',
		} );
	};

	const handleCalculationStatus = (
		type: InstallationCalculationType,
		status: boolean,
	) => {
		setIsCalculated( { ...isCalculated, [ type ]: status } );
	};

	const handleSubmit = ( event?: FormEvent ) => {
		if (event) {
			event.preventDefault();
		}

		if ( ! partState.partNumber ) {
			// Search Part
			setIsPartOpen( true );
		}
	};

	const updateFittings = ( results: Partial<FittingCalculationResults> ) => {
		setFittingResults( {
			...fittingResults,
			...results,
		} );
	};

	const preSearchClickHandler = () => {
		setIsPartSelected(true);
	};

	const setPartFromPreSearchAndSubmit = (value: string, e: React.MouseEvent<HTMLElement>) => {
		setSearchInput( value );
		handleSubmit(e);
	};

	return (
		<>
			<div>
				<form onSubmit={handleSubmit}>
					<div className="t-engineering-tools__form-fields t-engineering-tools__form-fields--multi-column timken-tool-presearch-container">
						<TextInput
							id="bearing_fitting_part_number"
							required={true}
							label={text.partNumber}
							verified={!! partState.partNumber}
							name="partNumber"
							value={searchInput}
							disabled={!! partState.partNumber}
							placeholder={text.partNumber}
							message={
								partState.partNumber
									? `${ text.bearingType }: ${ partState.bearingTypeLabel || partState.typeDescription }`
									: text.partWildCardHint
							}
							onChange={inputPartNumber}
						/>
						<PartPreSearch
							input={searchInput}
							isHidden={searchInput.length < 2 || isPartOpen || isPartSelected}
							unit={formState.unit}
							onClick={preSearchClickHandler}
							verifyPartNumber={(value: string, e) => setPartFromPreSearchAndSubmit(value, e)}
						/>

						<SelectDropdown
							id="bearing_fitting_unit"
							name="unit"
							required={true}
							label={text.displayUnits}
							value={formState.unit}
							options={unitList}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_unit',
									payload: event.target.value as TimkenUnit,
								} )
							}
						/>

						{! partState.partNumber && (
							<button
								className="btn btn--primary"
								type="submit"
								disabled={! searchInput}
							>
								{text.lookup}
							</button>
						)}

						{!! partState.partNumber && (
							<>
								<SelectDropdown
									id="bearing_fitting_class"
									name="bearingClass"
									required={true}
									label={text.bearingClass}
									tooltip={getTooltip( text.bearingClass )}
									value={formState.bearingClass?.value}
									options={bearingClasses.map( ( item ) => {
										return {
											id: item.value,
											value: item.value,
											label: item.displayLabel,
										} as SelectOption<string>;
									} )}
									loading={isBearingClassesLoading}
									onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
										dispatch( {
											type: 'change_bearing_class',
											payload: bearingClasses.find(
												( item ) => item.value === event.target.value,
											),
										} )
									}
								/>

								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => clearSearch()}
								>
									{text.clear}
								</button>
							</>
						)}
					</div>
				</form>
			</div>

			{partState.taperedBore === 'Y' && (
				<div className="t-engineering-tools__tapered-bore-message"
					 dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(taperedBoreMessage)}}
				/>
			)}

			{!! partState.partNumber && !! formState.bearingClass && partState.taperedBore === 'N' && (
				<>
					{isNavigationLoading && ( <FormLoader /> )}

					{! isNavigationLoading && (
						<>
							{isError && (
								<div className="t-engineering-tools__no-results">
									{text.noFittingInfo}
								</div>
							)}

							{! isError && (
								<>
									{!! innerNav && (
										<FittingForm
											unit={formState.unit}
											type={'Inner'}
											bearing={partState}
											bearingClass={formState.bearingClass}
											navigation={innerNav}
											onCalculate={handleCalculationStatus}
											onComplete={updateFittings}
										/>
									)}

									<div className="t-engineering-tools__divider"></div>

									{!! outerNav && (
										<FittingForm
											unit={formState.unit}
											type={'Outer'}
											bearing={partState}
											bearingClass={formState.bearingClass}
											navigation={outerNav}
											onCalculate={handleCalculationStatus}
											onComplete={updateFittings}
										/>
									)}
								</>
							)}
						</>
					)}
				</>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch
					units={formState.unit}
					partNumber={searchInput}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
				/>
			)}
		</>
	);
};

export default FittingPractice;
